import {
    AssignmentStatus,
    type Assignment,
    type AssignmentInitialSettings,
    type EditingAssignmentState,
    type State,
    type Student,
    type StudentList,
} from "@khan/assignments-types";
import {
    BuildQualifiedContentDescriptor,
    emptyAssignment,
    getMinDate,
    getValidDueDate,
} from "@khan/assignments-utils";

import type {Action} from "./actions.ts";

const defaultEditingAssignmentState: EditingAssignmentState = {
    qualifiedContentDescriptorsList: [],
    classDescriptors: [],
    assignment: null,
    initialSettings: {},
    preselectStudents: true,
};

export function emptyState(): State {
    return {
        editingAssignment: defaultEditingAssignmentState,
        students: {},
        studentLists: [],
        saving: false,
        isKhanmigoEnabled: false,
        hasGroupedExercises: false,
    };
}

// This method returns a single student list or null.
function getStudentList(state: State): StudentList | null | undefined {
    const {studentLists} = state;
    const initialClassDescriptors = getInitialClassDescriptors(state);
    // StudentLists are not loaded yet
    // or Multiple classes are selected in initialSettings.
    if (
        !studentLists ||
        !studentLists.length ||
        initialClassDescriptors.length > 1
    ) {
        return;
    }

    // No initial list was requested! Just return the first one.
    if (initialClassDescriptors.length === 0) {
        return studentLists[0];
    }

    // We already filtered possibly deleted classDescriptors in initialSettings
    // so it's safe to return the find result.
    return studentLists.find(
        ({descriptor}) => descriptor === initialClassDescriptors[0],
    );
}

// This method returns all currently-selected students
// Will either return the ones specified by the `initialSettings`, or it
// will simply pick all the students for the given StudentList.
function getStudentsFromList({
    initialSettings,
    studentList,
    students,
}: {
    initialSettings: AssignmentInitialSettings;
    studentList: any;
    students: Array<Student> | null | undefined;
}): Array<Student> {
    if (!students || students.length === 0) {
        return [];
    }

    const {studentIds, classDescriptors} = initialSettings;
    let initialStudentIds;

    // Only care about intialSettings.studentIds when a single
    // class is selected and it is the given studentList.
    if (
        classDescriptors &&
        classDescriptors.length === 1 &&
        studentList &&
        classDescriptors[0] === studentList.descriptor
    ) {
        initialStudentIds = studentIds;
    }

    // If no specific students were set in initial settings, return all of them.
    if (!initialStudentIds) {
        return students;
    }

    return students.filter((student) =>
        (initialStudentIds || []).includes(student.kaid),
    );
}

function getCoachedStudents(
    studentsToFilter: Array<Student>,
    coachedStudents?: Array<Student> | null,
): Array<Student> {
    if (coachedStudents) {
        return studentsToFilter.filter((student) =>
            (coachedStudents || []).find((s) => s.kaid === student.kaid),
        );
    }
    return studentsToFilter;
}

function getInitialClassDescriptors(allState: State): Array<string> {
    // Return existing classDescriptors in initialSettings.
    // Note that this function filters out any deleted classroom descriptors.
    // If there's none given, return the first classroom's descriptor in an
    // array.
    const {studentLists, editingAssignment} = allState;
    let initialClassDescriptors =
        (editingAssignment &&
            editingAssignment.initialSettings &&
            editingAssignment.initialSettings.classDescriptors) ||
        [];

    initialClassDescriptors = initialClassDescriptors.filter(
        (classDescriptor) =>
            studentLists.find((list) => list.descriptor === classDescriptor),
    );

    if (initialClassDescriptors.length) {
        return initialClassDescriptors;
    }
    return studentLists && studentLists.length
        ? [studentLists[0].descriptor]
        : [];
}

const reducers = {
    // True when the assignment includes at least one exercise with grouped
    // assessment item, otherwiser false. This value is used in
    // question-set-radio.tsx to determine whether the QuestionSetRadio is
    // visible.
    hasGroupedExercises(state = false, action: Action): boolean {
        switch (action.type) {
            case "HAS_GROUPED_EXERCISES": {
                return true;
            }
            case "STANDARD_EXERCISES_ONLY": {
                return false;
            }
            default:
                return state;
        }
    },
    isKhanmigoEnabled(state = false, action: Action): boolean {
        switch (action.type) {
            case "KHANMIGO_ENABLED":
                return true;
            default:
                return state;
        }
    },
    saving(state = false, action: Action): boolean {
        switch (action.type) {
            case "MUTATION_BEGIN":
                return true;

            case "MUTATION_END":
                return false;

            default:
                return state;
        }
    },

    editingAssignment(
        state: EditingAssignmentState = defaultEditingAssignmentState,
        action: Action,
        allState: State = emptyState(),
    ): EditingAssignmentState {
        switch (action.type) {
            case "START_EDITING_NEW_ASSIGNMENT": {
                // Whether we prefer students specified in
                // action.initialSettings over action.assignment
                const preselectStudents =
                    typeof action.preselectStudents === "undefined"
                        ? state.preselectStudents
                        : action.preselectStudents;

                let newAssignment = {...action.assignment};

                // Prefer studentList in action.assignment over
                // action.initialSettings
                const givenStudentList = action.assignment.studentList;
                const initialClassDescriptors = givenStudentList
                    ? [givenStudentList.descriptor]
                    : getInitialClassDescriptors({
                          ...allState,
                          editingAssignment: {
                              ...allState.editingAssignment,
                              initialSettings: action.initialSettings,
                          },
                      });

                // Single class case:
                // determine which students within the class to assign to
                if (initialClassDescriptors.length === 1) {
                    const descriptor = initialClassDescriptors[0];
                    const studentList = allState.studentLists.find(
                        (list) => list.descriptor === descriptor,
                    ) || {descriptor};
                    const students = preselectStudents
                        ? getStudentsFromList({
                              initialSettings: action.initialSettings,
                              studentList,
                              students: allState.students[descriptor],
                          })
                        : newAssignment.students;

                    newAssignment = {
                        ...newAssignment,
                        studentList,
                        students,
                    };
                }

                return {
                    qualifiedContentDescriptorsList:
                        action.assignment.qualifiedContentDescriptors || [],
                    classDescriptors: initialClassDescriptors,
                    initialSettings: action.initialSettings,
                    assignment: {
                        ...emptyAssignment(),
                        ...newAssignment,
                    },
                    preselectStudents,
                };
            }

            case "START_EDITING_EXISTING_ASSIGNMENT":
                const existingClassDescriptor =
                    action.props.studentList &&
                    action.props.studentList.descriptor;
                return {
                    ...state,
                    classDescriptors: existingClassDescriptor
                        ? [existingClassDescriptor]
                        : [],
                    assignment: {
                        ...action.props,
                        students: getCoachedStudents(
                            action.props.students,
                            allState.students[existingClassDescriptor],
                        ),
                    },
                };

            case "START_EDITING_DUPLICATE_ASSIGNMENT":
                const existing = action.props as Assignment;

                const duplicateClassDescriptor =
                    (existing.studentList && existing.studentList.descriptor) ||
                    "";

                // Build the qualified content descriptors list for this
                // assignment.  This is needed so that when we go to create
                // a new assignment, we can pull the correct position.
                const qualifiedContentDescriptorList = [
                    BuildQualifiedContentDescriptor(
                        existing.domainId,
                        existing.courseId,
                        existing.unitId,
                        existing.lessonId,
                        existing.contentDescriptors[0],
                    ),
                ];

                return {
                    ...state,
                    preselectStudents: false,
                    classDescriptors: duplicateClassDescriptor
                        ? [duplicateClassDescriptor]
                        : [],
                    qualifiedContentDescriptorsList:
                        qualifiedContentDescriptorList,
                    assignment: {
                        ...emptyAssignment(),
                        ...existing,
                        students: getCoachedStudents(
                            existing.students,
                            allState.students[duplicateClassDescriptor],
                        ),
                        startDate: getMinDate(existing.startDate).toDate(),
                        dueDate: getValidDueDate(
                            existing.startDate,
                            existing.dueDate,
                        ),
                        // clear out some things that we don't want to duplicate
                        id: undefined,
                        isDraft: true,
                        assignmentStatus: AssignmentStatus.DRAFT,
                    },
                };

            case "EDIT_CLASSDESCRIPTORS": {
                let students: Array<Student> = [];
                let studentList: StudentList | undefined = undefined;

                if (action.classDescriptors.length === 1) {
                    const classDescriptors = action.classDescriptors[0];
                    studentList = allState.studentLists.find(
                        (list) => list.descriptor === classDescriptors,
                    );
                    students = allState.students[classDescriptors] || [];
                }

                return {
                    ...state,
                    classDescriptors: action.classDescriptors,
                    // @ts-expect-error - TS2322 - Type 'AssignmentInitialSettings | undefined' is not assignable to type 'AssignmentInitialSettings'.
                    initialSettings: action.initialSettings,
                    // @ts-expect-error - TS2322 - Type '{ students: never[]; studentList: StudentList | undefined; id?: string | null | undefined; key?: string | undefined; isDraft?: boolean | undefined; assignedDate?: Date | undefined; ... 14 more ...; lessonId?: string | undefined; }' is not assignable to type 'Assignment'.
                    assignment: {
                        ...state.assignment,
                        students,
                        studentList,
                    },
                };
            }

            case "EDIT_ASSIGNMENT": {
                return {
                    ...state,
                    initialSettings: action.initialSettings || {},
                    assignment: {
                        ...state.assignment,
                        ...action.assignment,
                        configuredActivityInputs: {
                            ...state.assignment?.configuredActivityInputs,
                            ...action.assignment.configuredActivityInputs,
                        },
                    },
                };
            }

            case "SET_CONTENT_DESCRIPTORS":
                return {
                    ...state,
                    qualifiedContentDescriptorsList:
                        action.qualifiedContentDescriptorsList,
                };

            case "STOP_EDITING_ASSIGNMENT":
                return defaultEditingAssignmentState;

            case "FETCHED_STUDENTLISTS": {
                // If we get the student lists from apollo, set them all now
                const {assignment, initialSettings, preselectStudents} = state;

                const {studentLists} = action;

                if (assignment && assignment.studentList) {
                    const {studentList} = assignment;

                    const studentListFromAction = studentLists.find(
                        (list) => list.id === studentList.id,
                    );
                    const studentsInStudentList =
                        studentListFromAction &&
                        studentListFromAction.studentKaidsAndNicknames;
                    let nextStudents;

                    if (assignment.students.length) {
                        // If we get students and there were students in the
                        // assignment, we want to filter out students we're no
                        // longer coaching.

                        nextStudents = getCoachedStudents(
                            assignment.students,
                            studentList.studentKaidsAndNicknames,
                        );
                    } else if (
                        !assignment.students.length &&
                        studentsInStudentList
                    ) {
                        // If we currently had 0 students selected, and our
                        // state indicates that we want to preselect all
                        // students, select them (if there are any to select).
                        nextStudents = getStudentsFromList({
                            initialSettings,
                            studentList,
                            students: studentsInStudentList,
                        });
                    } else {
                        // In all other cases, no work is necessary.
                        nextStudents = assignment.students;
                    }

                    return {
                        ...state,
                        classDescriptors: [studentList.descriptor],
                        assignment: {
                            ...assignment,
                            students: nextStudents,
                        },
                    };
                }

                // If we get the student lists from apollo, set the student
                // list if there is only one
                const noStudentListYet = assignment && !assignment.studentList;

                if (
                    assignment &&
                    noStudentListYet &&
                    studentLists &&
                    studentLists.length > 0
                ) {
                    const studentList = getStudentList({
                        ...allState,
                        studentLists,
                    });

                    const students =
                        preselectStudents &&
                        getStudentsFromList({
                            initialSettings,
                            studentList,
                            students: studentList
                                ? studentList.studentKaidsAndNicknames
                                : [],
                        });

                    return {
                        ...state,
                        classDescriptors: getInitialClassDescriptors({
                            ...allState,
                            studentLists,
                        }),
                        assignment: {
                            ...assignment,
                            studentList,
                            students: students || [],
                        },
                    };
                }

                return state;
            }

            default:
                return state;
        }
    },

    students(
        state: Record<string, Array<Student>> = {},
        action: Action,
    ): Record<string, Array<Student>> {
        switch (action.type) {
            case "FETCHED_STUDENTLISTS":
                const {studentLists} = action;

                const studentsMap = studentLists.reduce<Record<string, any>>(
                    (result: any, studentList: StudentList) => ({
                        ...result,
                        [studentList.descriptor]:
                            studentList.studentKaidsAndNicknames || [],
                    }),
                    {},
                );

                return {
                    ...state,
                    ...studentsMap,
                };

            default:
                return state;
        }
    },

    studentLists(
        state: Array<StudentList> = [],
        action: Action,
    ): Array<StudentList> {
        switch (action.type) {
            case "FETCHED_STUDENTLISTS":
                return action.studentLists;

            default:
                return state;
        }
    },
} as const;

export {reducers};
